import {objectFromEntries} from '@benchling/js-utils';
import React, {useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import notifications from 'notifications';
import {addStudyables} from 'studies/StudyableDAO';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {
  StudyablesStudyIdsQuery,
  StudyablesStudyIdsQueryVariables,
} from './__generated/StudyablesStudyIdsQuery';
import {STUDYABLES_STUDY_IDS_QUERY} from './StudiesListingColumn';

export interface AddToStudiesModalProps {
  itemIds: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: () => Promise<void>;
  initialStudySearchItems?: Array<StudySearchItem>;
}

export default function AddToStudiesModal({
  itemIds,
  onRequestClose,
  onRequestSubmit,
  initialStudySearchItems,
}: AddToStudiesModalProps): JSX.Element {
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(
    initialStudySearchItems ?? []
  );

  async function addStudyablesAndUpdate(): Promise<void> {
    const studyIds = studySearchItems.map((study) => study.id);
    const studyables = objectFromEntries(itemIds.map((id) => [id, studyIds]));
    await addStudyables({studyables});
    await doQuerySafe<StudyablesStudyIdsQuery, StudyablesStudyIdsQueryVariables>(
      {query: STUDYABLES_STUDY_IDS_QUERY},
      {
        variables: {studyableIds: itemIds},
        fetchPolicy: 'network-only',
      }
    );
  }

  async function handleSubmit(): Promise<void> {
    await withLoadingScreen(addStudyablesAndUpdate());
    notifications.success(`Successfully added ${getItemStudyCountText()}`);
    await onRequestSubmit();
  }

  function getItemStudyCountText(): string {
    const numItems = itemIds.length;
    const numStudies = studySearchItems.length;
    return `${numItems} ${pluralize(numItems, 'item')} to ${numStudies} ${pluralize(numStudies, 'study')}`;
  }

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          submit: true,
          text: `Add ${getItemStudyCountText()}`,
          disabled: studySearchItems.length === 0,
        },
      ]}
      title="Add to studies"
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <AddToStudiesContents
        selectedItems={studySearchItems}
        onChange={(studies) => {
          setStudySearchItems(studies);
        }}
      />
    </Modal>
  );
}

interface AddToStudyContentsProps {
  onChange: (studies: Array<StudySearchItem>) => void;
  selectedItems: Array<StudySearchItem>;
}

export function AddToStudiesContents(props: AddToStudyContentsProps): JSX.Element {
  return (
    <Row>
      <Col md={12}>
        <FieldLabel label="Studies" />
        <StudySearchInput
          selectedItems={props.selectedItems}
          onChange={(studies) => {
            props.onChange(studies);
          }}
          shouldAllowMultiple={true}
        />
      </Col>
    </Row>
  );
}
